import { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Save, RefreshCw, Info, Factory, Wrench, AlertTriangle, ListChecks } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import PlantManagement from './PlantManagement';
import ReturnReasonsConfig from './ReturnReasonsConfig';
import QtyChangeReasonsConfig from './QtyChangeReasonsConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SystemSettings = () => {
  const [activeTab, setActiveTab] = useState('plants');
  const [bufferDays, setBufferDays] = useState(2);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings/system`);
      setBufferDays(response.data.mro_recalc_buffer_days);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const days = parseInt(bufferDays);
    if (isNaN(days) || days < 0 || days > 10) {
      toast.error('Buffer days must be between 0 and 10');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/settings/system`, { mro_recalc_buffer_days: days });
      toast.success('Settings saved successfully!', {
        description: `MRO recalculation buffer set to ${days} days`,
        duration: 4000
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-slate-50 to-blue-50 p-6 rounded-lg border border-slate-200">
        <div className="flex items-center gap-3 mb-2">
          <Settings className="w-6 h-6 text-slate-700" />
          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>System Settings</h2>
        </div>
        <p className="text-slate-600">Manage plants, system parameters, and operational settings</p>
      </div>

      <Card className="shadow-lg">
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6 max-w-2xl">
              <TabsTrigger data-testid="tab-plants" value="plants">
                <Factory className="w-4 h-4 mr-2" />
                Plants
              </TabsTrigger>
              <TabsTrigger data-testid="tab-return-reasons" value="return-reasons">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Return Reasons
              </TabsTrigger>
              <TabsTrigger data-testid="tab-qty-change-reasons" value="qty-change-reasons">
                <ListChecks className="w-4 h-4 mr-2" />
                Qty Reasons
              </TabsTrigger>
              <TabsTrigger data-testid="tab-general" value="general">
                <Wrench className="w-4 h-4 mr-2" />
                General
              </TabsTrigger>
            </TabsList>

            <TabsContent value="plants" data-testid="tab-content-plants">
              <PlantManagement />
            </TabsContent>

            <TabsContent value="return-reasons" data-testid="tab-content-return-reasons">
              <ReturnReasonsConfig />
            </TabsContent>

            <TabsContent value="qty-change-reasons" data-testid="tab-content-qty-change-reasons">
              <QtyChangeReasonsConfig />
            </TabsContent>

            <TabsContent value="general" data-testid="tab-content-general">
              <Card className="max-w-2xl">
                <CardHeader>
                  <CardTitle>Operational Parameters</CardTitle>
                  <CardDescription>Configure system-wide operational parameters</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-start gap-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-blue-900">
                        <p className="font-medium mb-1">MRO Recalculation Buffer</p>
                        <p className="text-blue-700">
                          Controls which day's MROs get recalculated after production completion.
                        </p>
                        <p className="text-blue-700 mt-2">
                          <strong>Example:</strong> If production completes on Jan 20 and buffer is 2 days,
                          the system will recalculate MROs for Jan 22 (not Jan 21).
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bufferDays">MRO Recalculation Buffer (Days)</Label>
                      <div className="flex gap-2">
                        <Input
                          id="bufferDays"
                          type="number"
                          min="0"
                          max="10"
                          value={bufferDays}
                          onChange={(e) => setBufferDays(e.target.value)}
                          className="w-32"
                          disabled={loading}
                        />
                        <Button onClick={handleSave} disabled={saving || loading} className="gap-2">
                          {saving ? (
                            <><RefreshCw className="w-4 h-4 animate-spin" />Saving...</>
                          ) : (
                            <><Save className="w-4 h-4" />Save</>
                          )}
                        </Button>
                      </div>
                      <p className="text-sm text-gray-600">Valid range: 0 to 10 days. Default: 2 days.</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Current Configuration</h4>
                    <div className="bg-gray-50 p-3 rounded-md">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">MRO Recalculation Buffer:</span>
                        <span className="font-medium">{bufferDays} {bufferDays === 1 ? 'day' : 'days'}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemSettings;
